import * as React from "react";
import NavigationItem from "./navigationitem";
import TitleLogo from "./titlelogo";
import Email from "./socials/email";
import LinkedIn from "./socials/linkedin";
import Phone from "./socials/phone";
import { useLocation } from "@reach/router";
import { LanguageContext } from "./layout";

const { useState } = React;

const NavigationBar = () => {
  const location = useLocation();
  const { lang, translations, setLang } = React.useContext(LanguageContext);
  const [isOpen, setIsOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  const line =
    "h-1 w-8 my-1 rounded-full bg-white transition ease transform duration-300";

  const pathName = location.pathname;

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    if (pathName !== "/") {
      setActiveSection("");
      return;
    }

    const sections = ["about", "hire", "references", "code", "blog"]
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null);

    if (sections.length === 0) {
      return;
    }

    let rafId = 0;
    const headerOffset = 96;

    const updateActiveSection = () => {
      let currentId = "";

      sections.forEach((section) => {
        const sectionTop = section.getBoundingClientRect().top;
        if (sectionTop <= headerOffset) {
          currentId = section.id;
        }
      });

      setActiveSection(currentId);
    };

    const requestUpdate = () => {
      if (rafId) {
        return;
      }

      rafId = window.requestAnimationFrame(() => {
        updateActiveSection();
        rafId = 0;
      });
    };

    requestUpdate();

    window.addEventListener("scroll", requestUpdate, { passive: true });
    window.addEventListener("resize", requestUpdate);
    window.addEventListener("orientationchange", requestUpdate);

    return () => {
      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }
      window.removeEventListener("scroll", requestUpdate);
      window.removeEventListener("resize", requestUpdate);
      window.removeEventListener("orientationchange", requestUpdate);
    };
  }, [pathName]);

  React.useEffect(() => {
    setIsOpen(false);
  }, [pathName]);

  React.useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }

    document.body.style.overflow = isOpen ? "hidden" : "";

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const getHashId = (link: string) => {
    const hash = link.split("#")[1];
    return hash ?? "";
  };

  const isNavActive = (link: string) => {
    if (link.startsWith("/#")) {
      return pathName === "/" && activeSection === getHashId(link);
    }

    if (link.startsWith("/apps")) {
      return pathName.startsWith("/apps");
    }

    if (link.startsWith("/privacy")) {
      return pathName.startsWith("/privacy");
    }

    if (link.startsWith("/imprint")) {
      return pathName.startsWith("/imprint");
    }

    return false;
  };

  return (
    <nav className="flex h-full w-full max-w-7xl flex-row items-center justify-between bg-transparent p-4">
      <div className="h-fit content-center items-center justify-center">
        <TitleLogo callback={() => setIsOpen(false)} />
      </div>
      {/*burger menu*/}
      <button
        type="button"
        className="flex h-12 w-12 flex-col items-center justify-center hover:cursor-pointer md:hidden"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Toggle menu"
        aria-expanded={isOpen}
      >
        <span
          className={`${line} ${isOpen ? "translate-y-3 rotate-45" : ""}`}
        />
        <span className={`${line} ${isOpen ? "opacity-0" : ""}`} />
        <span
          className={`${line} ${isOpen ? "-translate-y-3 -rotate-45" : ""}`}
        />
      </button>
      {/*desktop navigation*/}
      <ul className="hidden h-16 bg-transparent md:relative md:top-0 md:right-0 md:flex md:h-fit md:w-fit md:flex-row md:justify-center md:space-y-0 md:bg-inherit">
        <li>
          <NavigationItem title={translations[lang].navAbout} link="/#about" isActive={isNavActive("/#about")} />
        </li>
        <li>
          <NavigationItem title={translations[lang].navHire} link="/#hire" isActive={isNavActive("/#hire")} />
        </li>
        <li>
          <NavigationItem title={translations[lang].navReferences} link="/#references" isActive={isNavActive("/#references")} />
        </li>
        <li>
          <NavigationItem title={translations[lang].navCode} link="/#code" isActive={isNavActive("/#code")} />
        </li>
        {/* <li>
          <NavigationItem
          title="Blog"
          link="https://blog.ewers-peters.de/"
          isExternal={true}
          />
        </li> */}
        <li>
          <NavigationItem title={translations[lang].navBlog} link="/#blog" isActive={isNavActive("/#blog")} />
        </li>
        <li>
          <NavigationItem title={translations[lang].navApps} link="/apps#top" isActive={isNavActive("/apps#top")} />
        </li>
      </ul>
      {/*mobile navigation*/}
      <div
        className={`fixed inset-x-0 top-[82px] z-40 flex h-[calc(100dvh-82px)] w-full flex-col justify-start overflow-y-hidden bg-main px-2 pt-[4vh] pb-[8vh] transition-transform duration-500 md:hidden ${
          isOpen
            ? "translate-x-0 pointer-events-auto"
            : "translate-x-full pointer-events-none"
        }`}
      >
        <ul className="flex min-h-full w-full flex-col justify-start space-y-1">
          <li>
            <NavigationItem
              title={translations[lang].navAbout}
              link="/#about"
              callback={() => setIsOpen(false)}
            />
          </li>
          <li>
            <NavigationItem
              title={translations[lang].navHire}
              link="/#hire"
              callback={() => setIsOpen(false)}
            />
          </li>
          <li>
            <NavigationItem
              title={translations[lang].navReferences}
              link="/#references"
              callback={() => setIsOpen(false)}
            />
          </li>
          <li>
            <NavigationItem
              title={translations[lang].navCode}
              link="/#code"
              callback={() => setIsOpen(false)}
            />
          </li>
          {/* <li>
            <NavigationItem
            title="Blog"
            link="https://blog.ewers-peters.de/"
            isExternal={true}
            />
          </li> */}
          <li>
            <NavigationItem
              title={translations[lang].navBlog}
              link="/#blog"
              callback={() => setIsOpen(false)}
            />
          </li>
          <li>
            <NavigationItem
              title={translations[lang].navApps}
              link="/apps#top"
              callback={() => setIsOpen(false)}
            />
          </li>
          <li>
            <div className="mx-auto my-2 h-[1px] w-[40vw] bg-tertiary" />
          </li>
          <li>
            <div className="mx-auto flex w-fit content-start items-start justify-center p-1 2xs:p-2">
              <div className="flex h-full w-full content-center justify-center">
                <ul className="mx-auto flex h-full flex-row content-start items-start space-x-4">
                  <li>
                    <Phone className="h-8 w-8 fill-primary transition duration-200 ease-in-out hover:scale-125 hover:fill-highlight md:h-10 md:w-10" />
                  </li>
                  <li>
                    <Email className="h-8 w-8 fill-primary transition duration-200 ease-in-out hover:scale-125 hover:fill-highlight md:h-10 md:w-10" />
                  </li>
                  <li>
                    <LinkedIn className="h-8 w-8 fill-primary transition duration-200 ease-in-out hover:scale-125 hover:fill-highlight md:h-10 md:w-10" />
                  </li>
                </ul>
              </div>
            </div>
          </li>
          <li>
            <NavigationItem
              title={translations[lang].navPrivacy}
              link="/privacy#top"
              callback={() => setIsOpen(false)}
            />
          </li>
          <li>
            <NavigationItem
              title={translations[lang].footerImprint}
              link="/imprint#top"
              callback={() => setIsOpen(false)}
            />
          </li>
          <li>
            <div className="mx-auto flex w-fit content-center items-center justify-center p-2">
              <button
                className="h-8 w-auto px-2 rounded-md flex items-center justify-center transition duration-200 ease-in-out hover:scale-110 border-[1.5px] border-primary hover:border-highlight bg-transparent"
                onClick={() => setLang(lang === "en" ? "de" : "en")}
                aria-label="Switch language"
                title={lang === "en" ? "Switch to German" : "Switch to English"}
              >
                <span className="text-primary font-semibold text-sm">
                  {lang === "en" ? "EN" : "DE"}
                </span>
              </button>
            </div>
          </li>
        </ul>
      </div>
    </nav>
  );
};

export default NavigationBar;
